import React, { useState } from 'react';
import { Box, Typography, Button, Paper, Stack, Chip, Divider, Switch, FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';

const MarketingPosterPage = () => {
  const theme = useTheme();
  const [concept, setConcept] = useState(0);
  const [isEnglish, setIsEnglish] = useState(false);

  const concepts = [
    {
      title: "لا تسجل وأنت مغمض!",
      subtitle: "شوف تقييمات دكاترة جامعة الأمير سلطان قبل التسجيل",
      englishTitle: "Don't Register Blindly",
      englishSubtitle: "Check PSU professor ratings before you build your schedule",
      color: theme.palette.primary.main,
      accent: theme.palette.secondary.main,
      type: 'pain-point',
      bgStyle: 'clean'
    },
    {
      title: "صوتك مسموع في معيار",
      subtitle: "شارك تجربتك وساعد غيرك",
      englishTitle: "Your Voice Matters",
      englishSubtitle: "Share your experience and help others",
      color: '#0f172a', // Dark slate
      accent: theme.palette.primary.light,
      type: 'community',
      bgStyle: 'clean'
    },
    {
      title: "دليلك الشامل للمحاضرين",
      subtitle: "أكثر من ٥٠٠ محاضر وتقييم في مكان واحد",
      englishTitle: "Your Ultimate Guide",
      englishSubtitle: "500+ Professors and ratings in one place",
      color: theme.palette.primary.dark,
      accent: '#fbbf24', // Amber/Gold
      type: 'feature',
      bgStyle: 'clean'
    }
  ];

  const currentConcept = concepts[concept];

  // Mock Professor Card Data
  const mockProfessor = {
    name: "Dr. Abdullah",
    dept: "Computer Science",
    rating: 4.8,
    difficulty: 2.5,
    takeAgain: "98%"
  };

  const getBackgroundStyles = (style, color, accent) => {
      if (style === 'gradient') {
          return {
            background: `linear-gradient(135deg, ${color} 0%, ${accent} 100%)`,
            color: '#fff'
          };
      }
      if (style === 'dark-texture') {
          return {
            bgcolor: '#0f172a',
            backgroundImage: `radial-gradient(${accent}20 1px, transparent 1px), radial-gradient(${accent}20 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 10px 10px',
            color: '#fff'
          };
      }
      if (style === 'noise') {
           return {
             bgcolor: '#fff',
             backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E"), linear-gradient(to bottom right, #fff, #f1f5f9)`,
             color: color
           };
      }
      return {
          bgcolor: 'background.paper',
          color: color
      };
  };

  const bgStyles = getBackgroundStyles(currentConcept.bgStyle, currentConcept.color, currentConcept.accent);
  const isDark = currentConcept.bgStyle === 'gradient' || currentConcept.bgStyle === 'dark-texture';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#e2e8f0', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      
      {/* Controls */}
      <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', maxWidth: 800 }}>
        <Typography variant="h6">Poster Generator</Typography>
        
        <FormControlLabel
          control={<Switch checked={isEnglish} onChange={() => setIsEnglish(!isEnglish)} />}
          label="English Mode"
        />

        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {concepts.map((c, i) => (
             <Button 
                key={i}
                variant={concept === i ? "contained" : "outlined"} 
                onClick={() => setConcept(i)}
                size="small"
                sx={{ m: 0.5 }}
             >
                {c.type}
             </Button>
          ))}
        </Stack>
      </Paper>

      {/* The Poster Canvas (Instagram Portrait 4:5 ratio - 1080x1350 scaled down for view) */}
      <Box 
        id="poster-canvas"
        sx={{ 
          width: '540px', // Scaled down 50% from 1080
          height: '675px', // Scaled down 50% from 1350
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 0,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          transform: 'scale(1)',
          display: 'flex',
          flexDirection: 'column',
          ...bgStyles
        }}
      >
        {/* Decorative Elements for Clean Style Only */}
        {currentConcept.bgStyle === 'clean' && (
            <>
                <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', bgcolor: currentConcept.accent, opacity: 0.1 }} />
                <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: 300, height: 300, borderRadius: '50%', bgcolor: currentConcept.color, opacity: 0.1 }} />
            </>
        )}
        
        {/* Poster Content */}
        <Box sx={{ flex: 1, p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
          
          {/* Header / Logo Area */}
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pt: 2, alignItems: 'center', gap: 2 }}>
             {/* Invert logo for dark backgrounds if needed, or use white logo */}
            <Box 
              component="img"
              src="/logo192.png" 
              alt="Logo" 
              sx={{ 
                width: 60, 
                height: 60,
                borderRadius: 2
              }} 
            />
            <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#fff' : currentConcept.color, letterSpacing: -1 }}>
              {isEnglish ? 'Mear' : 'مِعْيَار'} <span style={{ color: currentConcept.accent }}>.</span>
            </Typography>
          </Box>

          {/* Main Headline */}
          <Box sx={{ textAlign: 'center', my: 4 }}>
             <Typography variant="h3" dir={isEnglish ? "ltr" : "rtl"} sx={{ 
               fontFamily: isEnglish ? 'Inter, sans-serif' : 'IBM Plex Sans Arabic', 
               fontWeight: 800, 
               color: isDark ? '#fff' : '#1e293b',
               lineHeight: 1.1,
               mb: 2,
               fontSize: '2.5rem',
               textShadow: isDark ? '0 2px 10px rgba(0,0,0,0.2)' : 'none',
               textAlign: 'center'
             }}>
              {isEnglish ? currentConcept.englishTitle : currentConcept.title}
            </Typography>
            <Typography variant="h5" dir={isEnglish ? "ltr" : "rtl"} sx={{ 
                color: isDark ? 'rgba(255,255,255,0.9)' : 'text.secondary', 
                fontWeight: 500, 
                px: 4, 
                textAlign: 'center',
                fontFamily: isEnglish ? 'Inter, sans-serif' : 'IBM Plex Sans Arabic'
            }}>
              {isEnglish ? currentConcept.englishSubtitle : currentConcept.subtitle}
            </Typography>
          </Box>

          {/* Visual Hook: The "Perfect Professor" Card Mockup */}
          <Paper 
            elevation={isDark ? 8 : 4}
            sx={{ 
              width: '100%', 
              maxWidth: 380, // Slightly reduced to prevent overflow
              borderRadius: 3,
              overflow: 'hidden',
              transform: 'rotate(-2deg)',
              transition: 'transform 0.3s',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'divider',
              bgcolor: isDark ? 'rgba(255,255,255,0.95)' : '#fff',
              mx: 2 // Ensure margin on small screens
            }}
          >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: isDark ? 'rgba(255,255,255,0.95)' : '#fff' }}>
               <Box sx={{ 
                 width: 56, height: 56, borderRadius: '50%', 
                 bgcolor: 'primary.50', color: 'primary.main',
                 display: 'flex', alignItems: 'center', justifyContent: 'center'
               }}>
                 <PersonIcon sx={{ fontSize: 28 }} />
               </Box>
               <Box>
                 <Typography variant="subtitle1" fontWeight="bold" color="text.primary">{mockProfessor.name}</Typography>
                 <Typography variant="body2" color="text.secondary">{mockProfessor.dept}</Typography>
               </Box>
               <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', color: '#fbbf24' }}>
                   <Typography fontWeight="bold" variant="h6" sx={{ color: '#1e293b', mr: 0.5 }}>{mockProfessor.rating}</Typography>
                   <StarIcon />
                 </Box>
               </Box>
            </Box>
            
            <Divider />
            
            <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-around' }}>
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="caption" color="text.secondary">Take Again</Typography>
                 <Typography variant="subtitle2" fontWeight="bold" color="success.main">{mockProfessor.takeAgain}</Typography>
               </Box>
               <Box sx={{ textAlign: 'center' }}>
                 <Typography variant="caption" color="text.secondary">Difficulty</Typography>
                 <Typography variant="subtitle2" fontWeight="bold" color="text.primary">{mockProfessor.difficulty}</Typography>
               </Box>
            </Box>
            
            <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.95)' : '#fff', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" label="Respectful" sx={{ bgcolor: 'primary.50', color: 'primary.main', fontWeight: 600 }} />
              <Chip size="small" label="Amazing Lectures" sx={{ bgcolor: 'primary.50', color: 'primary.main', fontWeight: 600 }} />
            </Box>
          </Paper>

          {/* Footer Call to Action */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, pb: 2 }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: isDark ? '#fff' : currentConcept.color, 
                color: isDark ? currentConcept.color : '#fff',
                fontWeight: 800,
                fontSize: '1.4rem',
                px: 6,
                py: 1.5,
                borderRadius: 50,
                mb: 2,
                boxShadow: isDark ? '0 10px 20px rgba(0,0,0,0.2)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                    bgcolor: isDark ? '#f1f5f9' : currentConcept.color,
                }
              }}
            >
              www.mear.sa
            </Button>
            <Typography variant="body2" color={isDark ? 'rgba(255,255,255,0.8)' : "text.secondary"} fontWeight={500} sx={{ fontFamily: isEnglish ? 'Inter, sans-serif' : 'IBM Plex Sans Arabic' }}>
              {isEnglish ? "Available now for PSU Students" : "متاح الآن لطلاب جامعة الأمير سلطان"}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
        Note: This is a preview. To save, take a screenshot of the card area above.
      </Typography>
    </Box>
  );
};

export default MarketingPosterPage;